import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Download } from "lucide-react";
import { api } from "../api";
import { useLang } from "../i18n";
import { useAuth } from "../context/AuthContext";
import { StatusBadge } from "../components/StatusBadge";
import { Button } from "../components/ui/button";

const RISK_COLOR = { low: "low", medium: "medium", elevated: "elevated", high: "pending" };

export default function ProPage() {
  const { t } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [saved, setSaved] = useState(null);

  useEffect(() => {
    if (user === null) return;
    if (user === false) { navigate("/logowanie"); return; }
    api.get("/saved-parcels").then((r) => setSaved(r.data.results)).catch(() => setSaved([]));
  }, [user, navigate]);

  if (!user || saved === null) return <div className="p-20 text-center text-slate-500">{t("common.loading")}</div>;

  const exportCsv = () => {
    const header = "Parcel;Gmina;Plan Ogólny;MPZP;Risk;Last Change;Next Deadline";
    const rows = saved.map((s) => [
      s.parcel.full_number, s.parcel.gmina_name, s.parcel.plan_ogolny_status,
      s.parcel.mpzp_status, s.parcel.change_risk, s.last_checked, "",
    ].join(";"));
    const blob = new Blob([[header, ...rows].join("\n")], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "plangmina-portfolio.csv";
    a.click();
  };

  return (
    <div data-testid="pro-page" className="mx-auto max-w-7xl px-4 py-10 sm:px-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight">{t("pro.title")}</h1>
          <p className="mt-1 text-sm text-slate-600">{t("pro.subtitle")}</p>
        </div>
        <Button data-testid="pro-export-csv-button" variant="outline" onClick={exportCsv} disabled={saved.length === 0}>
          <Download size={15} className="mr-2" /> {t("pro.export")}
        </Button>
      </div>

      {saved.length === 0 ? (
        <div className="mt-8 rounded-xl border border-dashed bg-white p-12 text-center">
          <p className="text-sm text-slate-500">{t("pro.empty")}</p>
          <Button data-testid="pro-find-parcel-button" className="mt-4 bg-[#DC143C] text-white hover:bg-[#B91C1C]" onClick={() => navigate("/")}>
            {t("dash.find")}
          </Button>
        </div>
      ) : (
        <div data-testid="pro-portfolio-table" className="mt-8 overflow-x-auto rounded-xl border bg-white">
          <table className="w-full text-sm">
            <thead className="bg-slate-900 text-left text-xs uppercase tracking-wide text-white">
              <tr>
                <th className="px-4 py-3">{t("pro.table.parcel")}</th>
                <th className="px-4 py-3">{t("pro.table.gmina")}</th>
                <th className="px-4 py-3">{t("pro.table.plan")}</th>
                <th className="px-4 py-3">{t("pro.table.mpzp")}</th>
                <th className="px-4 py-3">{t("pro.table.risk")}</th>
                <th className="px-4 py-3">{t("pro.table.lastChange")}</th>
                <th className="px-4 py-3">{t("pro.table.nextDeadline")}</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {saved.map((s) => (
                <tr
                  key={s.id}
                  data-testid={`pro-row-${s.parcel_id}`}
                  className="cursor-pointer transition-colors hover:bg-slate-50"
                  onClick={() => navigate(`/dzialka/${s.parcel_id}`)}
                >
                  <td className="px-4 py-3 font-mono-data font-semibold text-slate-900">{s.parcel.full_number}</td>
                  <td className="px-4 py-3 text-slate-600">{s.parcel.gmina_name}</td>
                  <td className="px-4 py-3"><StatusBadge status={s.parcel.plan_ogolny_status} label={t(`status.${s.parcel.plan_ogolny_status}`)} /></td>
                  <td className="px-4 py-3"><StatusBadge status={s.parcel.mpzp_status} label={t(`mpzp.${s.parcel.mpzp_status}`)} /></td>
                  <td className="px-4 py-3"><StatusBadge status={RISK_COLOR[s.parcel.change_risk]} label={t(`risk.${s.parcel.change_risk}`)} /></td>
                  <td className="px-4 py-3 text-xs text-slate-600">{new Date(s.last_checked).toLocaleDateString("pl-PL")}</td>
                  <td className="px-4 py-3 text-xs font-semibold text-slate-600">
                    {s.next_deadline ? new Date(s.next_deadline).toLocaleDateString("pl-PL") : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
